import { Injectable } from '@angular/core';
import { ReservationRepository } from "../repositories/reservation.repository";
import { FilterService } from "./filterer/filter.service";
import { DataManipulationService } from "./data-manipulation.service";
import { Dtos } from "../utils/dtos";
import { Constants } from "../utils/constants";
import { PostPayloadInterface } from "../models/post-payload.interface";
import { KeycloakService } from "keycloak-angular";
import { OrderByModel } from "./filterer/orderBy.model";
import { MyStorageService } from "./my-storage.service";
import { GuestDiyRepository } from "../repositories/guest-diy.repository";
import { Utilities } from "../utils/utilities";


@Injectable({
  providedIn: 'root'
})
export class ReservationService {
  STATUS_TO_CALENDAR = ['ACCEPTED', 'SUCCEEDED', 'FULFILLED'];
  private allEdit: boolean;

  constructor(private repo: ReservationRepository,
              private guestDiyRepo: GuestDiyRepository,
              private filterService: FilterService,
              private dataManipulationService: DataManipulationService,
              private keycloakService: KeycloakService,
              private myStorageService: MyStorageService) {
    this.allEdit = this.keycloakService.getUserRoles().includes(Constants.SYSTEM_ADMIN_RULE);
  }

  success(id: number) {
    return this.repo.success(id);
  }

  notArrived(id: number) {
    return this.repo.notArrived(id);
  }

  getOneById(id: any) {
    const filter = this.filterService.filter(Dtos.RESERVATION.ReservationForRestaurantAppDTO)
      .equal('id', id)
      .create();
    return this.repo.getAllByFilter(filter);
  }

  getTypeCalendarByRestaurantHistoryIdAndReservationDate(restaurantHistoryId: number, from: string, isDelivery: boolean) {
    const filter = this.filterService.filter(Dtos.RESERVATION.ReservationForRestaurantAppDTO)
      .equal('restaurantHistory_id', restaurantHistoryId)
      .and()
      .equal('temporary', false)
      .and();
    if (isDelivery) {
      filter.equal('type', Constants.RESERVATION_TYPES.DELIVERY).and();
    } else {
      filter.notEqual('type', Constants.RESERVATION_TYPES.DELIVERY).and();
    }
    filter.equal('reservationDate', from)
      .and();

    this.STATUS_TO_CALENDAR.forEach((status, index) => {
      if (index < this.STATUS_TO_CALENDAR.length - 1) {
        filter.equal('reservationStatus', status).or();
      } else {
        filter.equal('reservationStatus', status);
      }
    });
    filter.orderBy(OrderByModel.asc("reservationTime"))
    return this.repo.getAllByFilter(filter.create());
  }


  decrementRestaurantReservationPersonCount(reservation: any, guestIdsToBeDeleted: number[]) {
    const payload: PostPayloadInterface = {
      id: reservation.id,
      type: "decrement-person-count",
      attribute: {
        reservationNumber: reservation.reservationNumber,
        newType: reservation.type,
        newPersonCount: reservation.personCount,
        guestIdsToBeDeleted: guestIdsToBeDeleted
      }
    };
    return this.repo.decrementRestaurantReservationPersonCount(payload.attribute.reservationNumber, payload);
  }

  public addExtraDataForOne(reservation: any) {
    this.dataManipulationService.addNameAttributeFromFirstAndLastName(reservation.billingData);
    reservation.reservationDateTime = reservation.reservationDate + "T" + reservation.reservationTime;
    reservation.isEvent = reservation.makeryEvent != undefined;
    return reservation;
  }

  getReservationArchiveAndIsEditList() {
    return JSON.parse(this.myStorageService.getFromCookies(Constants.STORAGE_KEYS.RESERVATION_ARCHIVE_LIST, '[]'));
  }

  saveReservationArchiveAndIsEditList(archiveList: any[]) {
    this.myStorageService.saveToCookies(Constants.STORAGE_KEYS.RESERVATION_ARCHIVE_LIST, JSON.stringify(archiveList));
  }

  createAndSaveReservationArchiveAndIsEditList(reservations: any[]) {
    let archiveList = [];
    reservations.forEach(reservation => {
      archiveList.push({
        id: reservation.id,
        archived: reservation.archived,
        isEdit: reservation.isEdit
      });
    });
    this.saveReservationArchiveAndIsEditList(archiveList);
  }

  reservationArchiveStatusChange(id: number) {
    let archiveList = this.getReservationArchiveAndIsEditList();
    archiveList.forEach(reservation => {
      if (reservation.id == id) {
        reservation.archived = !reservation.archived;
      }
    });
    this.saveReservationArchiveAndIsEditList(archiveList);
  }

  reservationIsEditStatusChange(id: number) {
    let archiveList = this.getReservationArchiveAndIsEditList();
    archiveList.forEach(reservation => {
      if (reservation.id == id) {
        reservation.isEdit = !reservation.isEdit;
      }
    });
    this.saveReservationArchiveAndIsEditList(archiveList);
  }

  /**
   * @deprecated The method should not be used
   */
  public saveGuestsForReservation(reservation: any) {
    let attribute = {
      "reservationNumber": reservation.reservationNumber,
      "guests": reservation.guests
    };
    let payload = {
      id: 0,
      type: "guestsToReservation",
      attribute: attribute
    };
    return this.repo.saveGuestsToReservation(payload);
  }

  saveNewGuestWithDiy(reservationNumber: any, newGuestWithFood: any[]) {
    let payload = {
      id: 0,
      type: "guestsToReservation",
      attribute: {
        "reservationNumber": reservationNumber,
        "guests": newGuestWithFood
      }
    };
    return this.repo.saveNewGuestWithDiy(payload);
  }

  addNewDiyToGuest(guestId: number, diyId: number) {
    return this.repo.addNewDiyToGuest(guestId, diyId);
  }

  deleteDiyFromGuest(guestId: number, diyId: number) {
    return this.repo.deleteDiyFromGuest(guestId, diyId);
  }

  async diyToInProgress(ids: number[]) {
    if(ids.length<1){
      return;
    }
      await this.guestDiyRepo.inProgress(ids).toPromise();
  }

  async diyToSentOut(ids: number[]) {
    if(ids.length<1){
      return;
    }
      await this.guestDiyRepo.sentOut(ids).toPromise();
  }

  getDailySum(restaurantHistoryId, date: Date = new Date()) {
    return this.repo.getDailySum(restaurantHistoryId, Utilities.dateToBackendString(date));
  }

}
